import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Upload, X } from "lucide-react";

export interface FileUploadProps extends React.HTMLAttributes<HTMLDivElement> {
  onFilesSelected?: (files: File[]) => void;
  onFileRemove?: (file: File) => void;
  multiple?: boolean;
  accept?: string;
  maxSize?: number; // in bytes
  disabled?: boolean;
  variant?: "default" | "drag" | "compact";
  size?: "sm" | "md" | "lg";
}

export interface FileItem {
  file: File;
  id: string;
  progress?: number;
  status?: "uploading" | "success" | "error";
  error?: string;
}

const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      className,
      onFilesSelected,
      onFileRemove,
      multiple = false,
      accept,
      maxSize,
      disabled = false,
      variant = "default",
      size = "md",
      ...props
    },
    ref
  ) => {
    const [isDragOver, setIsDragOver] = React.useState(false);
    const [files, setFiles] = React.useState<FileItem[]>([]);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const dragCounterRef = React.useRef(0);

    // Validate file type based on accept prop
    const validateFileType = React.useCallback(
      (file: File): boolean => {
        if (!accept) return true;

        const acceptedTypes = accept.split(",").map((type) => type.trim());

        return acceptedTypes.some((type) => {
          if (type.startsWith(".")) {
            // File extension check
            const fileExtension =
              "." + file.name.split(".").pop()?.toLowerCase();
            return fileExtension === type.toLowerCase();
          } else if (type.includes("/*")) {
            // MIME type wildcard check
            const baseType = type.split("/")[0];
            return file.type.startsWith(baseType + "/");
          } else {
            // Exact MIME type check
            return file.type === type;
          }
        });
      },
      [accept]
    );

    // Validate file size
    const validateFileSize = React.useCallback(
      (file: File): boolean => {
        if (!maxSize) return true;
        return file.size <= maxSize;
      },
      [maxSize]
    );

    const handleFileSelect = React.useCallback(
      (selectedFiles: FileList | null) => {
        if (!selectedFiles) return;

        const fileArray = Array.from(selectedFiles);
        const validFiles: File[] = [];
        const errors: string[] = [];

        fileArray.forEach((file) => {
          if (!validateFileType(file)) {
            errors.push(`File "${file.name}" has an unsupported type`);
            return;
          }

          if (!validateFileSize(file)) {
            errors.push(
              `File "${file.name}" is too large. Max size: ${formatFileSize(
                maxSize!
              )}`
            );
            return;
          }

          validFiles.push(file);
        });

        // Show errors in console for debugging
        if (errors.length > 0) {
          console.warn("File validation errors:", errors);
        }

        if (validFiles.length === 0) return;

        const newFileItems: FileItem[] = validFiles.map((file) => ({
          file,
          id: Math.random().toString(36).substr(2, 9),
          status: "uploading" as const,
          progress: 0,
        }));

        setFiles((prev) => [...prev, ...newFileItems]);
        onFilesSelected?.(validFiles);
      },
      [maxSize, onFilesSelected, validateFileType, validateFileSize]
    );

    const handleDragEnter = React.useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) {
          dragCounterRef.current++;
          setIsDragOver(true);
        }
      },
      [disabled]
    );

    const handleDragOver = React.useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) {
          e.dataTransfer.dropEffect = "copy";
        }
      },
      [disabled]
    );

    const handleDragLeave = React.useCallback((e: React.DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragOver(false);
      }
    }, []);

    const handleDrop = React.useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsDragOver(false);
        if (!disabled) {
          handleFileSelect(e.dataTransfer.files);
        }
      },
      [disabled, handleFileSelect]
    );

    const handleFileInputChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileSelect(e.target.files);
        // Reset input value to allow selecting the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
      [handleFileSelect]
    );

    const handleFileRemove = React.useCallback(
      (fileItem: FileItem) => {
        setFiles((prev) => prev.filter((f) => f.id !== fileItem.id));
        onFileRemove?.(fileItem.file);
      },
      [onFileRemove]
    );

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const getFileIcon = (file: File) => {
      const extension = file.name.split(".").pop()?.toLowerCase();
      switch (extension) {
        case "pdf":
          return "📄";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
        case "svg":
          return "🖼️";
        case "doc":
        case "docx":
          return "📝";
        case "xls":
        case "xlsx":
          return "📊";
        case "ppt":
        case "pptx":
          return "📈";
        case "zip":
        case "rar":
        case "7z":
          return "📦";
        default:
          return "📎";
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          "wl-file-upload",
          `wl-file-upload-${variant}`,
          `wl-file-upload-${size}`,
          disabled && "wl-file-upload-disabled",
          className
        )}
        {...props}
      >
        {/* File Upload Area */}
        <div
          className={cn(
            "wl-file-upload-area",
            isDragOver && "wl-file-upload-area-drag-over",
            disabled && "wl-file-upload-area-disabled"
          )}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="wl-file-upload-content">
            <div className="wl-file-upload-icon">
              <Upload className="wl-file-upload-icon-svg" />
            </div>
            <h3 className="wl-file-upload-title">
              {isDragOver ? "Drop files here" : "Upload files"}
            </h3>
            <p className="wl-file-upload-description">
              {isDragOver
                ? "Release to upload"
                : "Drag and drop files here, or click to select files"}
            </p>
            {accept && (
              <p className="wl-file-upload-accept">
                Accepted formats: {accept}
              </p>
            )}
            {maxSize && (
              <p className="wl-file-upload-max-size">
                Max file size: {formatFileSize(maxSize)}
              </p>
            )}
            <Button
              className="wl-file-upload-button"
              disabled={disabled}
              onClick={() => !disabled && fileInputRef.current?.click()}
            >
              <Upload className="wl-file-upload-button-icon" />
              Choose Files
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple={multiple}
            accept={accept}
            onChange={handleFileInputChange}
            className="wl-file-upload-input"
            disabled={disabled}
          />
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="wl-file-upload-list">
            {files.map((fileItem) => (
              <div key={fileItem.id} className="wl-file-upload-item">
                <div className="wl-file-upload-item-content">
                  <div className="wl-file-upload-item-icon">
                    {getFileIcon(fileItem.file)}
                  </div>
                  <div className="wl-file-upload-item-info">
                    <p className="wl-file-upload-item-name">
                      {fileItem.file.name}
                    </p>
                    <p className="wl-file-upload-item-size">
                      {formatFileSize(fileItem.file.size)}
                    </p>
                    {fileItem.status === "uploading" && (
                      <div className="wl-file-upload-item-progress">
                        <div
                          className="wl-file-upload-item-progress-bar"
                          style={{ width: `${fileItem.progress || 0}%` }}
                        />
                      </div>
                    )}
                    {fileItem.status === "error" && (
                      <p className="wl-file-upload-item-error">
                        {fileItem.error || "Upload failed"}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="text"
                  size="sm"
                  className="wl-file-upload-item-remove"
                  onClick={() => handleFileRemove(fileItem)}
                  disabled={disabled}
                >
                  <X className="wl-file-upload-item-remove-icon" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

FileUpload.displayName = "FileUpload";

export { FileUpload };
